<template>
  <div class="guarantor-page">
    <PageHeader icon="fas fa-file-invoice-dollar">
      <template #title>Penjamin & Piutang</template>
      <template #subtitle
        >Asuransi, perusahaan, LoG, benefit, klaim, invoice kolektif,
        rekonsiliasi, dan corporate MCU</template
      >
      <template #actions
        ><button class="primary" @click="loadAll">
          <i class="fas fa-sync-alt"></i> Perbarui
        </button></template
      >
    </PageHeader>

    <nav class="tabs">
      <button
        v-for="item in tabs"
        :key="item.id"
        :class="{ active: tab === item.id }"
        @click="tab = item.id"
      >
        <i :class="item.icon"></i>{{ item.label
        }}<span v-if="item.count">{{ item.count }}</span>
      </button>
    </nav>
    <div v-if="error" class="alert">
      <i class="fas fa-exclamation-circle"></i>{{ error
      }}<button @click="error = ''">×</button>
    </div>

    <template v-if="tab === 'dashboard'">
      <section class="metrics">
        <article v-for="m in metrics" :key="m.label">
          <i :class="m.icon"></i>
          <div>
            <small>{{ m.label }}</small
            ><strong>{{ m.money ? money(m.value) : m.value }}</strong
            ><span>{{ m.help }}</span>
          </div>
        </article>
      </section>
      <div class="grid-2">
        <section class="panel">
          <Header
            title="Aging piutang penjamin"
            subtitle="Berdasarkan jatuh tempo klaim aktif"
          />
          <div class="aging" v-for="a in aging" :key="a.key">
            <span>{{ a.label }}</span>
            <div><i :style="{ width: a.width + '%' }"></i></div>
            <b>{{ money(a.value) }}</b>
          </div>
        </section>
        <section class="panel">
          <Header
            title="Pipeline klaim"
            subtitle="Jumlah dan nilai per status"
          />
          <div class="status-grid">
            <article
              v-for="s in dashboard.claim_statuses || []"
              :key="s.status"
            >
              <Status :value="s.status" /><b>{{ s.total }} klaim</b
              ><span>{{ money(s.amount) }}</span>
            </article>
            <div v-if="!(dashboard.claim_statuses || []).length" class="empty">
              Belum ada klaim.
            </div>
          </div>
        </section>
      </div>
      <section class="panel guidance">
        <Header
          title="Kontrol operasional yang aktif"
          subtitle="Guardrail dari registrasi sampai kas masuk"
        />
        <div>
          <span><i class="fas fa-check"></i>Kepesertaan & masa berlaku</span
          ><span><i class="fas fa-check"></i>Plafon used + reserved</span
          ><span><i class="fas fa-check"></i>Checklist sebelum submit</span
          ><span><i class="fas fa-check"></i>Workflow revisi/penolakan</span
          ><span><i class="fas fa-check"></i>Invoice satu penjamin</span
          ><span><i class="fas fa-check"></i>Alokasi bank per klaim</span
          ><span><i class="fas fa-check"></i>PPh, admin fee & write-off</span
          ><span><i class="fas fa-check"></i>Audit trail klaim</span>
        </div>
      </section>
    </template>

    <template v-else-if="tab === 'payers'">
      <Toolbar
        title="Master penjamin & produk"
        subtitle="Perusahaan, asuransi, TPA, plan/polis, dan tarif kontrak"
        button="Tambah Penjamin"
        @action="openForm('payer')"
      />
      <div class="grid-2">
        <section class="panel">
          <Header
            title="Penjamin"
            :subtitle="payers.length + ' mitra terdaftar'"
          /><Table :headers="['Kode', 'Nama', 'Jenis', 'Termin', 'Status', '']"
            ><tr v-for="p in payers" :key="p.id">
              <td>
                <b>{{ p.code }}</b>
              </td>
              <td>{{ p.name }}</td>
              <td>{{ label(p.type) }}</td>
              <td>{{ p.payment_term_days }} hari</td>
              <td><Status :value="p.is_active ? 'active' : 'inactive'" /></td>
              <td>
                <button class="icon" @click="openForm('plan', p)">
                  <i class="fas fa-layer-group"></i></button
                ><button class="icon" @click="openForm('tariff', p)">
                  <i class="fas fa-tags"></i>
                </button>
              </td></tr
          ></Table>
        </section>
        <section class="panel">
          <Header
            title="Plan & benefit"
            subtitle="Versi kontrak dan checklist dokumen"
          /><Table
            :headers="['Penjamin', 'Plan', 'Berlaku', 'Coverage', 'Plafon']"
            ><tr v-for="p in plans" :key="p.id">
              <td>{{ payerName(p.guarantor_payer_id) }}</td>
              <td>
                <b>{{ p.code }}</b
                ><br /><small>{{ p.name }}</small>
              </td>
              <td>
                {{ date(p.effective_from) }} –
                {{ p.effective_to ? date(p.effective_to) : "seterusnya" }}
              </td>
              <td>{{ p.default_coverage_percent }}%</td>
              <td>{{ money(p.annual_limit) }}</td>
            </tr></Table
          >
        </section>
      </div>
      <section class="panel">
        <Header
          title="Tarif khusus penjamin"
          subtitle="Contract price, coverage, co-payment, dan pre-authorization"
        /><Table
          :headers="[
            'Penjamin / Plan',
            'Layanan',
            'Tarif kontrak',
            'Coverage',
            'Co-pay',
            'Berlaku',
          ]"
          ><tr v-for="t in tariffs" :key="t.id">
            <td>
              {{ payerName(t.guarantor_payer_id) }}<br /><small>{{
                planName(t.guarantor_plan_id) || "Semua plan"
              }}</small>
            </td>
            <td>
              <b>{{ t.service_name }}</b
              ><br /><small
                >{{ label(t.service_type) }}
                {{ t.requires_preauthorization ? "· Pre-auth" : "" }}</small
              >
            </td>
            <td>{{ money(t.contract_price) }}</td>
            <td>{{ t.coverage_percent }}%</td>
            <td>{{ money(t.patient_copay) }}</td>
            <td>{{ date(t.effective_from) }}</td>
          </tr></Table
        >
      </section>
    </template>

    <template v-else-if="tab === 'members'">
      <Toolbar
        title="Kepesertaan & benefit pasien"
        subtitle="Nomor peserta, polis, principal, masa aktif, plafon, pemakaian, dan reservasi"
        button="Daftarkan Peserta"
        @action="openForm('coverage')"
      />
      <section class="panel">
        <Table
          :headers="[
            'Pasien',
            'Penjamin / Plan',
            'Nomor peserta',
            'Hubungan',
            'Masa berlaku',
            'Benefit',
            'Status',
          ]"
          ><tr v-for="c in coverages" :key="c.id">
            <td>
              <b>{{ c.patient_name }}</b
              ><br /><small>{{ c.is_primary ? "Penjamin utama" : "" }}</small>
            </td>
            <td>
              {{ c.payer_name }}<br /><small>{{
                c.plan_name || "Tanpa plan"
              }}</small>
            </td>
            <td>
              {{ c.member_number }}<br /><small>{{
                c.policy_number || c.employee_number
              }}</small>
            </td>
            <td>{{ label(c.relationship) }}</td>
            <td>
              {{ date(c.valid_from) }} –
              {{ c.valid_to ? date(c.valid_to) : "seterusnya" }}
            </td>
            <td>
              <b>{{ money(c.benefit_limit) }}</b
              ><br /><small
                >Terpakai {{ money(c.benefit_used) }} · Reserved
                {{ money(c.benefit_reserved) }}</small
              >
            </td>
            <td><Status :value="c.eligibility_status" /></td></tr
        ></Table>
      </section>
    </template>

    <template v-else-if="tab === 'letters'">
      <Toolbar
        title="Letter of Guarantee"
        subtitle="Reservasi plafon dan kontrol approval jaminan"
        button="Buat LoG"
        @action="openForm('letter')"
      />
      <section class="panel">
        <Table
          :headers="[
            'Nomor',
            'Pasien',
            'Penjamin',
            'Terbit / Berlaku',
            'Limit',
            'Reserved',
            'Status',
            '',
          ]"
          ><tr v-for="l in letters" :key="l.id">
            <td>
              <b>{{ l.number }}</b
              ><br /><small>{{ l.external_number }}</small>
            </td>
            <td>
              {{ l.patient_name }}<br /><small>{{ l.member_number }}</small>
            </td>
            <td>{{ l.payer_name }}</td>
            <td>
              {{ date(l.issued_date) }}<br /><small>{{
                l.valid_until ? datetime(l.valid_until) : "-"
              }}</small>
            </td>
            <td>{{ money(l.approved_limit) }}</td>
            <td>{{ money(l.reserved_amount) }}</td>
            <td><Status :value="l.status" /></td>
            <td>
              <button
                v-if="l.status === 'requested'"
                class="mini"
                @click="letterStatus(l, 'approved')"
              >
                Setujui
              </button>
            </td>
          </tr></Table
        >
      </section>
    </template>

    <template v-else-if="tab === 'claims'">
      <Toolbar
        title="Claim control center"
        subtitle="Checklist, submission, adjudikasi, revisi, penolakan, dan timeline audit"
        button="Buat Klaim"
        @action="openForm('claim')"
      />
      <section class="panel">
        <Table
          :headers="[
            'Klaim / Pasien',
            'Penjamin',
            'Pelayanan',
            'Ditagihkan',
            'Disetujui / Dibayar',
            'Status',
            '',
          ]"
          ><tr v-for="c in claims" :key="c.id">
            <td>
              <b>{{ c.claim_number }}</b
              ><br /><small>{{ c.patient_name }} · Rev {{ c.revision }}</small>
            </td>
            <td>
              {{ c.payer_name }}<br /><small>{{
                c.invoice_number || "Belum di-invoice"
              }}</small>
            </td>
            <td>
              {{ date(c.service_date) }}<br /><small
                >Jatuh tempo {{ date(c.due_date) }}</small
              >
            </td>
            <td>
              {{ money(c.covered_amount) }}<br /><small
                >Co-pay {{ money(c.patient_copay) }}</small
              >
            </td>
            <td>
              {{ money(c.approved_amount) }}<br /><small
                >Dibayar {{ money(c.paid_amount) }}</small
              >
            </td>
            <td><Status :value="c.status" /></td>
            <td>
              <button class="mini" @click="openClaim(c.id)">Proses</button>
            </td>
          </tr></Table
        >
      </section>
    </template>

    <template v-else-if="tab === 'invoices'">
      <Toolbar
        title="Invoice kolektif penjamin"
        subtitle="Gabungkan klaim approved per penjamin dan periode"
        button="Buat Invoice"
        @action="openForm('invoice')"
      />
      <section class="panel">
        <Table
          :headers="[
            'Invoice',
            'Penjamin',
            'Periode',
            'Jatuh tempo',
            'Total',
            'Dibayar',
            'Outstanding',
            'Status',
            '',
          ]"
          ><tr v-for="i in invoices" :key="i.id">
            <td>
              <b>{{ i.invoice_number }}</b
              ><br /><small>{{ date(i.invoice_date) }}</small>
            </td>
            <td>{{ i.payer_name }}</td>
            <td>{{ date(i.period_start) }} – {{ date(i.period_end) }}</td>
            <td>{{ date(i.due_date) }}</td>
            <td>{{ money(i.total_amount) }}</td>
            <td>{{ money(i.paid_amount) }}</td>
            <td>
              <b>{{ money(i.outstanding_amount) }}</b>
            </td>
            <td><Status :value="i.status" /></td>
            <td>
              <button
                v-if="i.status === 'draft'"
                class="mini"
                @click="sendInvoice(i)"
              >
                Kirim
              </button>
            </td>
          </tr></Table
        >
      </section>
    </template>

    <template v-else-if="tab === 'reconciliation'">
      <Toolbar
        title="Rekonsiliasi pembayaran"
        subtitle="Alokasikan mutasi bank ke klaim termasuk PPh, biaya admin, dan selisih klaim"
        button="Rekonsiliasi"
        @action="openForm('receipt')"
      />
      <section class="metrics small">
        <article>
          <i class="fas fa-coins"></i>
          <div>
            <small>Belum teralokasi</small
            ><strong>{{ money(dashboard.unallocated_receipts) }}</strong
            ><span>Penerimaan yang perlu ditindaklanjuti</span>
          </div>
        </article>
        <article>
          <i class="fas fa-exclamation-triangle"></i>
          <div>
            <small>Klaim ditolak/revisi</small
            ><strong>{{ money(dashboard.rejected_amount) }}</strong
            ><span>Perlu appeal atau write-off terotorisasi</span>
          </div>
        </article>
      </section>
      <section class="panel">
        <Table
          :headers="[
            'Penerimaan',
            'Penjamin',
            'Tanggal',
            'Referensi bank',
            'Jumlah',
            'Teralokasi',
            'Belum dialokasi',
          ]"
          ><tr v-for="r in receipts" :key="r.id">
            <td>
              <b>{{ r.receipt_number }}</b>
            </td>
            <td>{{ r.payer_name }}</td>
            <td>{{ date(r.receipt_date) }}</td>
            <td>{{ r.bank_reference || "-" }}</td>
            <td>{{ money(r.amount) }}</td>
            <td>{{ money(r.allocated_amount) }}</td>
            <td>
              <b>{{ money(r.unallocated_amount) }}</b>
            </td>
          </tr></Table
        >
      </section>
    </template>

    <template v-else-if="tab === 'mcu'">
      <Toolbar
        title="Corporate MCU"
        subtitle="Program, paket snapshot, target peserta, progress pemeriksaan, dan delivery hasil"
        button="Buat Program"
        @action="openForm('mcu')"
      />
      <section class="programs">
        <article v-for="m in mcuPrograms" :key="m.id">
          <div class="program-head">
            <span><i class="fas fa-briefcase-medical"></i></span
            ><Status :value="m.status" />
          </div>
          <h3>{{ m.name }}</h3>
          <p>{{ m.payer_name }} · {{ m.location || "Lokasi klinik" }}</p>
          <div class="progress">
            <i :style="{ width: progress(m) + '%' }"></i>
          </div>
          <small
            >{{ m.completed_count || 0 }} selesai ·
            {{ m.participant_count || 0 }} terdaftar · target
            {{ m.target_participants }}</small
          >
          <footer>
            <b>{{ money(m.price_per_participant) }} / peserta</b
            ><button class="mini" @click="openForm('participant', m)">
              Tambah Peserta
            </button>
          </footer>
        </article>
      </section>
    </template>

    <div v-if="formMode" class="overlay" @click.self="closeForm">
      <section class="guarantor-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <small>INPUT TERKONTROL</small>
            <h2>{{ formTitle }}</h2>
          </div>
          <button @click="closeForm">×</button>
        </header>
        <div class="form-body">
          <template v-if="formMode === 'payer'"
            ><Field label="Kode"><input v-model="form.code" /></Field
            ><Field label="Nama penjamin"><input v-model="form.name" /></Field
            ><Field label="Jenis"
              ><select v-model="form.type">
                <option value="insurance">Asuransi</option>
                <option value="corporate">Perusahaan</option>
                <option value="tpa">TPA</option>
                <option value="government">Pemerintah</option>
              </select></Field
            ><Field label="Termin (hari)"
              ><input
                type="number"
                v-model.number="form.payment_term_days" /></Field
            ><Field label="Credit limit"
              ><input type="number" v-model.number="form.credit_limit" /></Field
            ><Field label="Email billing"
              ><input type="email" v-model="form.email" /></Field
            ><Field label="Telepon"><input v-model="form.phone" /></Field
            ><Field label="NPWP"><input v-model="form.tax_id" /></Field
            ><label class="check"
              ><input type="checkbox" v-model="form.requires_log" /> Wajib
              Letter of Guarantee</label
            ></template
          >
          <template v-else-if="formMode === 'plan'"
            ><Field label="Penjamin"
              ><select v-model="form.guarantor_payer_id">
                <option v-for="p in payers" :value="p.id" :key="p.id">
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Kode plan"><input v-model="form.code" /></Field
            ><Field label="Nama plan"><input v-model="form.name" /></Field
            ><Field label="Mulai berlaku"
              ><input type="date" v-model="form.effective_from" /></Field
            ><Field label="Batas tahunan"
              ><input type="number" v-model.number="form.annual_limit" /></Field
            ><Field label="Batas per kunjungan"
              ><input
                type="number"
                v-model.number="form.per_visit_limit" /></Field
            ><Field label="Coverage default (%)"
              ><input
                type="number"
                v-model.number="form.default_coverage_percent" /></Field
            ><Field label="Co-pay default"
              ><input
                type="number"
                v-model.number="form.default_copay" /></Field
            ><Field wide label="Checklist dokumen (pisahkan koma)"
              ><input
                v-model="form.checklist_text"
                placeholder="LoG, Resume medis, Kuitansi, Hasil penunjang" /></Field
          ></template>
          <template v-else-if="formMode === 'tariff'"
            ><Field label="Penjamin"
              ><select v-model="form.guarantor_payer_id">
                <option v-for="p in payers" :value="p.id" :key="p.id">
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Plan"
              ><select v-model="form.guarantor_plan_id">
                <option :value="null">Semua plan</option>
                <option
                  v-for="p in plansFor(form.guarantor_payer_id)"
                  :value="p.id"
                  :key="p.id"
                >
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Jenis layanan"
              ><select v-model="form.service_type">
                <option v-for="v in serviceTypes" :value="v" :key="v">
                  {{ label(v) }}
                </option>
              </select></Field
            ><Field label="Nama layanan"
              ><input v-model="form.service_name" /></Field
            ><Field label="Tarif kontrak"
              ><input
                type="number"
                v-model.number="form.contract_price" /></Field
            ><Field label="Coverage (%)"
              ><input
                type="number"
                v-model.number="form.coverage_percent" /></Field
            ><Field label="Co-pay"
              ><input
                type="number"
                v-model.number="form.patient_copay" /></Field
            ><Field label="Mulai berlaku"
              ><input type="date" v-model="form.effective_from" /></Field
            ><label class="check"
              ><input
                type="checkbox"
                v-model="form.requires_preauthorization"
              />
              Perlu pre-authorization</label
            ></template
          >
          <template v-else-if="formMode === 'coverage'"
            ><Field label="Pasien"
              ><button
                type="button"
                class="lookup-trigger"
                aria-haspopup="dialog"
                @click="$bvModal.show('modal-patient')"
              >
                <span class="lookup-trigger__icon"><i class="far fa-user"></i></span>
                <span :class="{ placeholder: !selectedPatient }">
                  <b>{{ selectedPatient ? selectedPatient.name : 'Pilih pasien' }}</b>
                  <small>{{ selectedPatient ? patientIdentity(selectedPatient) : 'Cari berdasarkan nama, No. RM, atau NIK' }}</small>
                </span>
                <i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Penjamin"
              ><select v-model="form.guarantor_payer_id">
                <option v-for="p in payers" :value="p.id" :key="p.id">
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Plan"
              ><select v-model="form.guarantor_plan_id">
                <option :value="null">Tanpa plan</option>
                <option
                  v-for="p in plansFor(form.guarantor_payer_id)"
                  :value="p.id"
                  :key="p.id"
                >
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Nomor peserta"
              ><input v-model="form.member_number" /></Field
            ><Field label="Nomor polis"
              ><input v-model="form.policy_number" /></Field
            ><Field label="Nomor pegawai"
              ><input v-model="form.employee_number" /></Field
            ><Field label="Hubungan"
              ><select v-model="form.relationship">
                <option value="self">Diri sendiri</option>
                <option value="spouse">Pasangan</option>
                <option value="child">Anak</option>
                <option value="parent">Orang tua</option>
                <option value="other">Lainnya</option>
              </select></Field
            ><Field label="Mulai berlaku"
              ><input type="date" v-model="form.valid_from" /></Field
            ><Field label="Berlaku sampai"
              ><input type="date" v-model="form.valid_to" /></Field
            ><Field label="Plafon benefit"
              ><input
                type="number"
                v-model.number="form.benefit_limit" /></Field
            ><label class="check"
              ><input type="checkbox" v-model="form.is_primary" /> Penjamin
              utama</label
            ></template
          >
          <template v-else-if="formMode === 'letter'"
            ><Field label="Kepesertaan"
              ><button type="button" class="lookup-trigger" @click="openLookup('coverage')">
                <span class="lookup-trigger__icon"><i class="fas fa-id-card"></i></span>
                <span :class="{ placeholder: !selectedCoverage }">
                  <b>{{ selectedCoverage ? selectedCoverage.patient_name : 'Pilih kepesertaan' }}</b>
                  <small>{{ selectedCoverage ? coverageIdentity(selectedCoverage) : 'Lookup peserta aktif dan penjaminnya' }}</small>
                </span><i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Nomor eksternal"
              ><input v-model="form.external_number" /></Field
            ><Field label="Tanggal terbit"
              ><input type="date" v-model="form.issued_date" /></Field
            ><Field label="Berlaku sampai"
              ><input type="datetime-local" v-model="form.valid_until" /></Field
            ><Field label="Limit disetujui"
              ><input
                type="number"
                v-model.number="form.approved_limit" /></Field
            ><Field label="Nilai reservasi"
              ><input
                type="number"
                v-model.number="form.reserved_amount" /></Field
            ><Field wide label="Cakupan">
              <textarea v-model="form.coverage_notes"></textarea></Field
            ><Field wide label="Pengecualian">
              <textarea v-model="form.exclusions"></textarea></Field
          ></template>
          <template v-else-if="formMode === 'claim'"
            ><Field label="Kepesertaan"
              ><button type="button" class="lookup-trigger" @click="openLookup('coverage')">
                <span class="lookup-trigger__icon"><i class="fas fa-id-card"></i></span>
                <span :class="{ placeholder: !selectedCoverage }">
                  <b>{{ selectedCoverage ? selectedCoverage.patient_name : 'Pilih kepesertaan' }}</b>
                  <small>{{ selectedCoverage ? coverageIdentity(selectedCoverage) : 'Lookup peserta aktif dan penjaminnya' }}</small>
                </span><i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Tanggal pelayanan"
              ><input type="date" v-model="form.service_date" /></Field
            ><Field label="Jenis layanan"
              ><input :value="form.description ? label(form.service_type) : '-'" readonly /></Field
            ><Field label="Nama layanan"
              ><button type="button" class="lookup-trigger" :disabled="!selectedCoverage" @click="openLookup('service')">
                <span class="lookup-trigger__icon"><i class="fas fa-stethoscope"></i></span>
                <span :class="{ placeholder: !form.description }">
                  <b>{{ form.description || 'Pilih layanan kontrak' }}</b>
                  <small>{{ form.description ? serviceSelectionMeta : 'Pilih kepesertaan terlebih dahulu' }}</small>
                </span><i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Nilai tagihan"
              ><input
                type="number"
                v-model.number="form.billed_amount" /></Field
            ><Field label="Coverage (%)"
              ><input
                type="number"
                v-model.number="form.coverage_percent" /></Field
            ><Field wide label="Catatan">
              <textarea v-model="form.notes"></textarea></Field
          ></template>
          <template v-else-if="formMode === 'invoice'"
            ><Field label="Penjamin"
              ><button type="button" class="lookup-trigger" @click="openLookup('payer')">
                <span class="lookup-trigger__icon"><i class="fas fa-building"></i></span>
                <span :class="{ placeholder: !selectedPayer }">
                  <b>{{ selectedPayer ? selectedPayer.name : 'Pilih penjamin' }}</b>
                  <small>{{ selectedPayer ? payerIdentity(selectedPayer) : 'Lookup master penjamin aktif' }}</small>
                </span><i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Tanggal invoice"
              ><input type="date" v-model="form.invoice_date" /></Field
            ><Field label="Periode awal"
              ><input type="date" v-model="form.period_start" /></Field
            ><Field label="Periode akhir"
              ><input type="date" v-model="form.period_end"
            /></Field>
            <div class="claim-picker">
              <label v-for="c in invoiceCandidates" :key="c.id"
                ><input
                  type="checkbox"
                  :value="c.id"
                  v-model="form.claim_ids"
                /><span
                  ><b>{{ c.claim_number }}</b
                  >{{ c.patient_name }} · {{ money(c.approved_amount) }}</span
                ></label
              >
              <p v-if="!invoiceCandidates.length">
                Pilih penjamin yang memiliki klaim approved.
              </p>
            </div></template
          >
          <template v-else-if="formMode === 'receipt'"
            ><Field label="Penjamin"
              ><button type="button" class="lookup-trigger" @click="openLookup('payer')">
                <span class="lookup-trigger__icon"><i class="fas fa-building"></i></span>
                <span :class="{ placeholder: !selectedPayer }">
                  <b>{{ selectedPayer ? selectedPayer.name : 'Pilih penjamin' }}</b>
                  <small>{{ selectedPayer ? payerIdentity(selectedPayer) : 'Lookup master penjamin aktif' }}</small>
                </span><i class="fas fa-search lookup-trigger__arrow"></i>
              </button></Field
            ><Field label="Tanggal masuk"
              ><input type="date" v-model="form.receipt_date" /></Field
            ><Field label="Jumlah bank"
              ><input type="number" v-model.number="form.amount" /></Field
            ><Field label="Referensi bank"
              ><input v-model="form.bank_reference" /></Field
            ><Field label="Klaim"
              ><select v-model="form.guarantor_claim_id">
                <option v-for="c in reconCandidates" :value="c.id" :key="c.id">
                  {{ c.claim_number }} · {{ c.patient_name }}
                </option>
              </select></Field
            ><Field label="Kas dialokasikan"
              ><input
                type="number"
                v-model.number="form.allocation_amount" /></Field
            ><Field label="PPh dipotong"
              ><input
                type="number"
                v-model.number="form.withholding_tax" /></Field
            ><Field label="Biaya admin"
              ><input type="number" v-model.number="form.admin_fee" /></Field
            ><Field label="Selisih/write-off"
              ><input
                type="number"
                v-model.number="form.short_payment" /></Field
          ></template>
          <template v-else-if="formMode === 'mcu'"
            ><Field label="Perusahaan"
              ><select v-model="form.guarantor_payer_id">
                <option v-for="p in corporatePayers" :value="p.id" :key="p.id">
                  {{ p.name }}
                </option>
              </select></Field
            ><Field label="Nama program"><input v-model="form.name" /></Field
            ><Field label="Mulai"
              ><input type="date" v-model="form.start_date" /></Field
            ><Field label="Selesai"
              ><input type="date" v-model="form.end_date" /></Field
            ><Field label="Lokasi"><input v-model="form.location" /></Field
            ><Field label="Target peserta"
              ><input
                type="number"
                v-model.number="form.target_participants" /></Field
            ><Field label="Harga / peserta"
              ><input
                type="number"
                v-model.number="form.price_per_participant" /></Field
            ><Field wide label="Paket pemeriksaan (pisahkan koma)"
              ><input
                v-model="form.package_text"
                placeholder="Darah lengkap, Urinalisis, EKG, Rontgen" /></Field
          ></template>
          <template v-else-if="formMode === 'participant'"
            ><Field label="Nomor pegawai"
              ><input v-model="form.employee_number" /></Field
            ><Field label="Nama"><input v-model="form.name" /></Field
            ><Field label="NIK"><input v-model="form.id_card_number" /></Field
            ><Field label="Departemen"
              ><input v-model="form.department" /></Field
          ></template>
        </div>
        <footer>
          <button class="secondary" @click="closeForm">Batal</button
          ><button class="primary" :disabled="saving" @click="saveForm">
            <i v-if="saving" class="fas fa-spinner fa-spin"></i
            >{{ saving ? " Menyimpan" : "Simpan" }}
          </button>
        </footer>
      </section>
    </div>

    <div v-if="lookupMode" class="overlay lookup-overlay" @click.self="closeLookup">
      <section class="lookup-modal" role="dialog" aria-modal="true">
        <header>
          <div>
            <small>DATA MASTER</small>
            <h2>{{ lookupTitle }}</h2>
            <p>{{ lookupSubtitle }}</p>
          </div>
          <button type="button" aria-label="Tutup" @click="closeLookup">×</button>
        </header>
        <div class="lookup-search">
          <i class="fas fa-search"></i>
          <input v-model.trim="lookupSearch" type="search" :placeholder="lookupPlaceholder" autofocus />
          <span>{{ lookupRows.length }} data</span>
        </div>
        <div class="lookup-list">
          <button v-for="row in lookupRows" :key="lookupMode + '-' + row.id" type="button" @click="chooseLookup(row)">
            <span class="lookup-list__icon"><i :class="lookupIcon"></i></span>
            <span><b>{{ lookupPrimary(row) }}</b><small>{{ lookupSecondary(row) }}</small></span>
            <i class="fas fa-chevron-right"></i>
          </button>
          <p v-if="!lookupRows.length" class="empty">Data yang sesuai tidak ditemukan.</p>
        </div>
      </section>
    </div>

    <div
      v-if="selectedClaim"
      class="overlay"
      @click.self="selectedClaim = null"
    >
      <section
        class="guarantor-modal claim-modal"
        role="dialog"
        aria-modal="true"
      >
        <header>
          <div>
            <small>CLAIM WORKFLOW</small>
            <h2>{{ selectedClaim.claim_number }}</h2>
            <p>
              {{ selectedClaim.patient_name }} · {{ selectedClaim.payer_name }}
            </p>
          </div>
          <button @click="selectedClaim = null">×</button>
        </header>
        <div class="claim-summary">
          <div>
            <small>Ditagihkan</small
            ><b>{{ money(selectedClaim.billed_amount) }}</b>
          </div>
          <div>
            <small>Penjamin</small
            ><b>{{ money(selectedClaim.covered_amount) }}</b>
          </div>
          <div>
            <small>Co-pay</small><b>{{ money(selectedClaim.patient_copay) }}</b>
          </div>
          <div>
            <small>Status</small><Status :value="selectedClaim.status" />
          </div>
        </div>
        <div class="claim-columns">
          <section>
            <h3>Checklist dokumen</h3>
            <div
              v-for="d in selectedClaim.documents"
              :key="d.id"
              class="document"
            >
              <i
                :class="
                  d.status === 'verified'
                    ? 'fas fa-check-circle ok'
                    : 'far fa-circle'
                "
              ></i
              ><span
                ><b>{{ d.name }}</b
                ><small
                  >{{ label(d.status)
                  }}{{ d.is_required ? " · wajib" : "" }}</small
                ></span
              ><button
                v-if="d.status !== 'verified'"
                class="mini"
                @click="verifyDocument(d)"
              >
                Verifikasi
              </button>
            </div>
            <p v-if="!selectedClaim.documents.length" class="empty">
              Plan ini tidak mensyaratkan dokumen.
            </p>
            <h3>Item klaim</h3>
            <div v-for="i in selectedClaim.items" :key="i.id" class="line">
              <span>{{ i.description }} × {{ i.quantity }}</span
              ><b>{{ money(i.covered_amount) }}</b>
            </div>
          </section>
          <section>
            <h3>Langkah berikutnya</h3>
            <div class="workflow-actions">
              <button
                v-for="a in claimActions"
                :key="a.status"
                :class="a.tone"
                @click="transition(a)"
              >
                <i :class="a.icon"></i>{{ a.label }}
              </button>
            </div>
            <h3>Timeline audit</h3>
            <div class="timeline">
              <article v-for="e in selectedClaim.events" :key="e.id">
                <i></i>
                <div>
                  <b>{{ label(e.event_type) }}</b
                  ><span
                    >{{ label(e.from_status) }} → {{ label(e.to_status) }}</span
                  ><small
                    >{{ datetime(e.created_at) }} ·
                    {{ e.created_by_name }}</small
                  >
                  <p v-if="e.reason">{{ e.reason }}</p>
                </div>
              </article>
            </div>
          </section>
        </div>
      </section>
    </div>

    <ModalPatient
      source="guarantor-coverage"
      @chosenPatient="setCoveragePatient"
      @submitedPatient="setCoveragePatient"
    />
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service.js";
import PageHeader from "@/component/general/PageHeader.vue";
import ModalPatient from "@/component/general/ModalPatient.vue";
const Header = {
  props: ["title", "subtitle"],
  template:
    '<header class="section-head"><div><h3>{{title}}</h3><p>{{subtitle}}</p></div></header>',
};
const Toolbar = {
  props: ["title", "subtitle", "button"],
  template:
    '<header class="toolbar"><div><h2>{{title}}</h2><p>{{subtitle}}</p></div><button type="button" class="primary" @click="$emit(\'action\')"><i class="fas fa-plus"></i>{{button}}</button></header>',
};
const Table = {
  props: ["headers"],
  template:
    '<div class="table-wrap"><table><thead><tr><th v-for="h in headers" :key="h">{{h}}</th></tr></thead><tbody><slot></slot></tbody></table><div v-if="!$slots.default" class="empty">Belum ada data.</div></div>',
};
const Field = {
  props: ["label", "wide"],
  template:
    '<label :class="{wide:wide}"><span>{{label}}</span><slot></slot></label>',
};
const Status = {
  props: ["value"],
  template: "<span :class=\"['badge',value]\">{{text}}</span>",
  computed: {
    text() {
      return String(this.value || "-").replace(/_/g, " ");
    },
  },
};
export default {
  name: "GuarantorWorkspace",
  components: { PageHeader, ModalPatient, Header, Toolbar, Table, Field, Status },
  data() {
    return {
      tab: this.$route.query.tab || "dashboard",
      loading: false,
      saving: false,
      error: "",
      dashboard: {},
      masters: {},
      letters: [],
      claims: [],
      invoices: [],
      receipts: [],
      mcuPrograms: [],
      selectedPatient: null,
      lookupMode: null,
      lookupSearch: "",
      formMode: null,
      form: {},
      selectedClaim: null,
      serviceTypes: [
        "service",
        "medical_action",
        "lab",
        "medicine",
        "inventory",
        "mcu",
        "clinic",
      ],
    };
  },
  computed: {
    payers() {
      return this.masters.payers || [];
    },
    plans() {
      return this.masters.plans || [];
    },
    tariffs() {
      return this.masters.tariffs || [];
    },
    coverages() {
      return this.masters.coverages || [];
    },
    selectedCoverage() {
      return this.coverages.find(
        (c) => c.id == this.form.patient_guarantor_coverage_id
      ) || null;
    },
    selectedPayer() {
      return this.payers.find((p) => p.id == this.form.guarantor_payer_id) || null;
    },
    serviceCandidates() {
      if (!this.selectedCoverage) return [];
      return this.tariffs.filter(
        (t) =>
          t.is_active &&
          t.guarantor_payer_id == this.selectedCoverage.guarantor_payer_id &&
          (!t.guarantor_plan_id ||
            t.guarantor_plan_id == this.selectedCoverage.guarantor_plan_id)
      );
    },
    serviceSelectionMeta() {
      const tariff = this.tariffs.find(
        (t) =>
          t.service_type === this.form.service_type &&
          t.service_id == this.form.service_id &&
          t.service_name === this.form.description
      );
      return tariff
        ? `${this.label(tariff.service_type)} · ${this.money(tariff.contract_price)}`
        : this.label(this.form.service_type);
    },
    lookupTitle() {
      return {
        coverage: "Pilih kepesertaan",
        payer: "Pilih penjamin",
        service: "Pilih layanan kontrak",
      }[this.lookupMode] || "Pilih data";
    },
    lookupSubtitle() {
      return {
        coverage: "Peserta aktif beserta plan, penjamin, dan nomor kepesertaan.",
        payer: "Master penjamin aktif yang menjadi sumber relasi transaksi.",
        service: "Tarif layanan yang berlaku untuk penjamin dan plan peserta.",
      }[this.lookupMode] || "";
    },
    lookupPlaceholder() {
      return this.lookupMode === "coverage"
        ? "Cari pasien, nomor peserta, atau penjamin"
        : this.lookupMode === "service"
        ? "Cari nama atau kode layanan"
        : "Cari nama atau kode penjamin";
    },
    lookupIcon() {
      return {
        coverage: "fas fa-id-card",
        payer: "fas fa-building",
        service: "fas fa-stethoscope",
      }[this.lookupMode] || "fas fa-list";
    },
    lookupRows() {
      const source = this.lookupMode === "coverage"
        ? this.coverages.filter((c) => c.eligibility_status === "active")
        : this.lookupMode === "service"
        ? this.serviceCandidates
        : this.payers.filter((p) => p.is_active);
      const q = this.lookupSearch.toLowerCase();
      if (!q) return source;
      return source.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
    },
    corporatePayers() {
      return this.payers.filter((p) => p.type === "corporate");
    },
    tabs() {
      return [
        { id: "dashboard", label: "Dashboard", icon: "fas fa-chart-line" },
        {
          id: "payers",
          label: "Penjamin & Tarif",
          icon: "fas fa-building",
          count: this.payers.length,
        },
        {
          id: "members",
          label: "Kepesertaan",
          icon: "fas fa-id-card",
          count: this.coverages.length,
        },
        {
          id: "letters",
          label: "LoG",
          icon: "fas fa-file-signature",
          count: this.letters.length,
        },
        {
          id: "claims",
          label: "Klaim",
          icon: "fas fa-file-medical-alt",
          count: this.claims.length,
        },
        {
          id: "invoices",
          label: "Invoice Kolektif",
          icon: "fas fa-file-invoice",
          count: this.invoices.length,
        },
        {
          id: "reconciliation",
          label: "Rekonsiliasi",
          icon: "fas fa-university",
        },
        { id: "mcu", label: "Corporate MCU", icon: "fas fa-briefcase-medical" },
      ];
    },
    metrics() {
      return [
        {
          label: "Outstanding piutang",
          value: this.dashboard.outstanding || 0,
          money: true,
          icon: "fas fa-wallet",
          help: "Saldo klaim aktif",
        },
        {
          label: "Klaim terbuka",
          value: this.dashboard.open_claims || 0,
          icon: "fas fa-folder-open",
          help: "Belum selesai dibayar",
        },
        {
          label: "Klaim overdue",
          value: this.dashboard.overdue_claims || 0,
          icon: "fas fa-clock",
          help: "Lewat jatuh tempo",
        },
        {
          label: "Peserta aktif",
          value: this.dashboard.active_members || 0,
          icon: "fas fa-users",
          help: "Kepesertaan valid",
        },
      ];
    },
    aging() {
      const labels = {
          current: "Belum jatuh tempo",
          "1_30": "1–30 hari",
          "31_60": "31–60 hari",
          "61_90": "61–90 hari",
          over_90: "> 90 hari",
        },
        d = this.dashboard.aging || {},
        max = Math.max(1, ...Object.values(d).map(Number));
      return Object.keys(labels).map((k) => ({
        key: k,
        label: labels[k],
        value: d[k] || 0,
        width: Math.max(2, (Number(d[k] || 0) / max) * 100),
      }));
    },
    invoiceCandidates() {
      return this.claims.filter(
        (c) =>
          c.guarantor_payer_id == this.form.guarantor_payer_id &&
          ["approved", "partially_approved"].includes(c.status)
      );
    },
    reconCandidates() {
      return this.claims.filter(
        (c) =>
          c.guarantor_payer_id == this.form.guarantor_payer_id &&
          [
            "approved",
            "partially_approved",
            "invoiced",
            "partially_paid",
            "rejected",
            "revision_required",
          ].includes(c.status)
      );
    },
    formTitle() {
      return {
        payer: "Tambah penjamin",
        plan: "Tambah plan benefit",
        tariff: "Tambah tarif kontrak",
        coverage: "Daftarkan kepesertaan",
        letter: "Buat Letter of Guarantee",
        claim: "Buat klaim",
        invoice: "Buat invoice kolektif",
        receipt: "Rekonsiliasi penerimaan",
        mcu: "Buat program corporate MCU",
        participant: "Tambah peserta MCU",
      }[this.formMode];
    },
    claimActions() {
      if (!this.selectedClaim) return [];
      const map = {
        draft: [["ready", "Dokumen Siap", "fas fa-check", "primary"]],
        ready: [
          ["submitted", "Submit Klaim", "fas fa-paper-plane", "primary"],
          ["draft", "Kembali Draft", "fas fa-undo", "secondary"],
        ],
        submitted: [
          ["under_review", "Dalam Review", "fas fa-search", "primary"],
          ["revision_required", "Minta Revisi", "fas fa-edit", "warning"],
          ["rejected", "Tolak", "fas fa-times", "danger"],
        ],
        under_review: [
          ["approved", "Setujui", "fas fa-check", "primary"],
          [
            "partially_approved",
            "Setujui Sebagian",
            "fas fa-adjust",
            "warning",
          ],
          ["revision_required", "Minta Revisi", "fas fa-edit", "warning"],
          ["rejected", "Tolak", "fas fa-times", "danger"],
        ],
        revision_required: [
          ["draft", "Buka Revisi", "fas fa-edit", "primary"],
          ["submitted", "Kirim Ulang", "fas fa-paper-plane", "primary"],
        ],
        rejected: [
          ["revision_required", "Ajukan Revisi", "fas fa-redo", "warning"],
          ["cancelled", "Tutup Ditolak", "fas fa-ban", "danger"],
        ],
      };
      return (map[this.selectedClaim.status] || []).map((x) => ({
        status: x[0],
        label: x[1],
        icon: x[2],
        tone: x[3],
      }));
    },
  },
  watch: {
    tab(v) {
      this.$router
        .replace({ query: { ...this.$route.query, tab: v } })
        .catch(() => {});
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const [d, m, l, c, i, r, mc] = await Promise.all([
          ApiService.get("guarantors/dashboard"),
          ApiService.get("guarantors/masters"),
          ApiService.get("guarantors/letters"),
          ApiService.get("guarantors/claims"),
          ApiService.get("guarantors/invoices"),
          ApiService.get("guarantors/receipts"),
          ApiService.get("guarantors/mcu-programs"),
        ]);
        this.dashboard = d.data.data || {};
        this.masters = m.data.data || {};
        this.letters = l.data.data || [];
        this.claims = c.data.data || [];
        this.invoices = i.data.data || [];
        this.receipts = r.data.data || [];
        this.mcuPrograms = mc.data.data || [];
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loading = false;
      }
    },
    openForm(mode, parent) {
      const today = new Date().toISOString().slice(0, 10);
      this.formMode = mode;
      this.selectedPatient = null;
      this.closeLookup();
      const defaults = {
        payer: {
          type: "insurance",
          payment_term_days: 30,
          credit_limit: 0,
          requires_log: false,
          is_active: true,
        },
        plan: {
          guarantor_payer_id: parent && parent.id,
          default_coverage_percent: 100,
          default_copay: 0,
          annual_limit: 0,
          per_visit_limit: 0,
          effective_from: today,
          checklist_text: "LoG, Resume medis, Kuitansi",
        },
        tariff: {
          guarantor_payer_id: parent && parent.id,
          guarantor_plan_id: null,
          service_type: "service",
          coverage_percent: 100,
          patient_copay: 0,
          effective_from: today,
          requires_preauthorization: false,
        },
        coverage: {
          relationship: "self",
          valid_from: today,
          benefit_limit: 0,
          eligibility_status: "active",
          is_primary: true,
        },
        letter: {
          issued_date: today,
          status: "requested",
          approved_limit: 0,
          reserved_amount: 0,
        },
        claim: {
          service_date: today,
          service_type: "service",
          coverage_percent: 100,
          billed_amount: 0,
        },
        invoice: {
          invoice_date: today,
          period_start: today.slice(0, 8) + "01",
          period_end: today,
          claim_ids: [],
        },
        receipt: {
          receipt_date: today,
          amount: 0,
          allocation_amount: 0,
          withholding_tax: 0,
          admin_fee: 0,
          short_payment: 0,
        },
        mcu: {
          start_date: today,
          end_date: today,
          target_participants: 1,
          price_per_participant: 0,
          status: "draft",
        },
        participant: { program_id: parent && parent.id, status: "registered" },
      };
      this.form = defaults[mode] || {};
    },
    closeForm() {
      this.formMode = null;
      this.form = {};
    },
    async saveForm() {
      this.saving = true;
      this.error = "";
      try {
        let route = "",
          payload = { ...this.form };
        if (this.formMode === "payer") route = "guarantors/payers";
        if (this.formMode === "plan") {
          route = "guarantors/plans";
          payload.document_checklist = (payload.checklist_text || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
          payload.benefit_rules = [];
        }
        if (this.formMode === "tariff") route = "guarantors/tariffs";
        if (this.formMode === "coverage") route = "guarantors/coverages";
        if (this.formMode === "letter") route = "guarantors/letters";
        if (this.formMode === "claim") {
          route = "guarantors/claims";
          payload.items = [
            {
              service_type: payload.service_type,
              service_id: payload.service_id || null,
              service_code: payload.service_code || null,
              description: payload.description,
              quantity: 1,
              unit_price: payload.billed_amount,
              billed_amount: payload.billed_amount,
              coverage_percent: payload.coverage_percent,
            },
          ];
        }
        if (this.formMode === "invoice") route = "guarantors/invoices";
        if (this.formMode === "receipt") {
          route = "guarantors/reconcile";
          payload.allocations = [
            {
              guarantor_claim_id: payload.guarantor_claim_id,
              amount: payload.allocation_amount,
              withholding_tax: payload.withholding_tax,
              admin_fee: payload.admin_fee,
              short_payment: payload.short_payment,
            },
          ];
        }
        if (this.formMode === "mcu") {
          route = "guarantors/mcu-programs";
          payload.package_snapshot = (payload.package_text || "")
            .split(",")
            .map((x) => x.trim())
            .filter(Boolean);
        }
        if (this.formMode === "participant")
          route = `guarantors/mcu-programs/${payload.program_id}/participants`;
        await ApiService.post(route, payload);
        this.closeForm();
        await this.loadAll();
      } catch (e) {
        this.handleError(e);
      } finally {
        this.saving = false;
      }
    },
    openLookup(mode) {
      if (mode === "service" && !this.selectedCoverage) return;
      this.lookupMode = mode;
      this.lookupSearch = "";
    },
    closeLookup() {
      this.lookupMode = null;
      this.lookupSearch = "";
    },
    chooseLookup(row) {
      if (this.lookupMode === "coverage") {
        this.form.patient_guarantor_coverage_id = row.id;
        this.form.patient_id = row.patient_id;
        if (this.formMode === "claim") {
          this.form.description = "";
          this.form.service_id = null;
          this.form.service_code = null;
        }
      } else if (this.lookupMode === "payer") {
        this.form.guarantor_payer_id = row.id;
        this.form.claim_ids = [];
        this.form.guarantor_claim_id = null;
      } else if (this.lookupMode === "service") {
        this.form.service_type = row.service_type;
        this.form.service_id = row.service_id;
        this.form.service_code = row.service_code;
        this.form.description = row.service_name;
        this.form.billed_amount = Number(row.contract_price || 0);
        this.form.coverage_percent = Number(row.coverage_percent || 100);
      }
      this.closeLookup();
    },
    setCoveragePatient(patient) {
      this.selectedPatient = patient;
      this.form.patient_id = patient.id;
    },
    patientIdentity(patient) {
      const medicalRecord = patient.patient_id || patient.medical_record_number;
      const nik = patient.id_card_number || patient.nik;
      if (medicalRecord && nik) return `No. RM ${medicalRecord} · NIK ${nik}`;
      if (medicalRecord) return `No. RM ${medicalRecord}`;
      if (nik) return `NIK ${nik}`;
      return "Pasien terpilih";
    },
    coverageIdentity(coverage) {
      return `${coverage.payer_name} · ${coverage.plan_name || "Tanpa plan"} · ${coverage.member_number}`;
    },
    payerIdentity(payer) {
      return `${payer.code} · ${this.label(payer.type)} · termin ${payer.payment_term_days} hari`;
    },
    lookupPrimary(row) {
      if (this.lookupMode === "coverage") return row.patient_name;
      return this.lookupMode === "service" ? row.service_name : row.name;
    },
    lookupSecondary(row) {
      if (this.lookupMode === "coverage") return this.coverageIdentity(row);
      if (this.lookupMode === "service") {
        return `${row.service_code || this.label(row.service_type)} · ${this.money(row.contract_price)} · coverage ${Number(row.coverage_percent)}%`;
      }
      return this.payerIdentity(row);
    },
    async letterStatus(l, status) {
      try {
        await ApiService.post(`guarantors/letters/${l.id}/status?_method=PUT`, {
          status,
        });
        await this.loadAll();
      } catch (e) {
        this.handleError(e);
      }
    },
    async sendInvoice(i) {
      try {
        await ApiService.post(`guarantors/invoices/${i.id}/send`, {});
        await this.loadAll();
      } catch (e) {
        this.handleError(e);
      }
    },
    async openClaim(id) {
      try {
        const r = await ApiService.get(`guarantors/claims/${id}`);
        this.selectedClaim = r.data.data;
      } catch (e) {
        this.handleError(e);
      }
    },
    async verifyDocument(d) {
      try {
        await ApiService.post(
          `guarantors/claims/${this.selectedClaim.id}/documents`,
          { ...d, status: "verified" }
        );
        await this.openClaim(this.selectedClaim.id);
      } catch (e) {
        this.handleError(e);
      }
    },
    async transition(a) {
      let payload = { status: a.status };
      if (["approved", "partially_approved"].includes(a.status)) {
        const v = window.prompt(
          "Nilai yang disetujui penjamin",
          this.selectedClaim.covered_amount
        );
        if (v === null) return;
        payload.approved_amount = Number(v);
      }
      if (["rejected", "revision_required", "cancelled"].includes(a.status)) {
        const reason = window.prompt("Alasan wajib dicatat");
        if (!reason) return;
        payload.reason = reason;
      }
      try {
        await ApiService.post(
          `guarantors/claims/${this.selectedClaim.id}/transition`,
          payload
        );
        await this.openClaim(this.selectedClaim.id);
        await this.loadAll();
      } catch (e) {
        this.handleError(e);
      }
    },
    handleError(e) {
      const data = e && e.response && e.response.data;
      this.error =
        (data && data.message) ||
        Object.values((data && data.errors) || {})
          .flat()
          .join(" ") ||
        "Proses gagal. Periksa data lalu coba lagi.";
    },
    payerName(id) {
      const x = this.payers.find((p) => p.id == id);
      return x ? x.name : "-";
    },
    planName(id) {
      const x = this.plans.find((p) => p.id == id);
      return x ? x.name : "";
    },
    plansFor(id) {
      return this.plans.filter((p) => p.guarantor_payer_id == id);
    },
    money(v) {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      }).format(Number(v || 0));
    },
    date(v) {
      return v
        ? new Intl.DateTimeFormat("id-ID", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }).format(new Date(v))
        : "-";
    },
    datetime(v) {
      return v
        ? new Intl.DateTimeFormat("id-ID", {
            dateStyle: "medium",
            timeStyle: "short",
          }).format(new Date(v))
        : "-";
    },
    label(v) {
      return String(v || "-")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (x) => x.toUpperCase());
    },
    progress(m) {
      return Math.min(
        100,
        (Number(m.completed_count || 0) /
          Math.max(1, Number(m.target_participants || 1))) *
          100
      );
    },
  },
};
</script>

<style scoped>
.guarantor-page {
  --ink: #17213d;
  --muted: #74809b;
  --line: #e7eaf2;
  --blue: #087f78;
  --soft: #f5f7fc;
  color: var(--ink);
  padding-bottom: 50px;
}
.primary,
.secondary,
.mini,
.icon {
  border: 0;
  border-radius: 9px;
  cursor: pointer;
  font-weight: 700;
}
.primary {
  background: linear-gradient(135deg, #145a88, #12988c);
  color: #fff;
  padding: 11px 17px;
}
.primary i {
  margin-right: 7px;
}
.secondary {
  background: #eef1f7;
  color: var(--ink);
  padding: 11px 17px;
}
.tabs {
  background: #edf3f6;
  border-radius: 12px;
  display: flex;
  gap: 5px;
  margin: 16px 0 22px;
  overflow-x: auto;
  padding: 6px;
  width: max-content;
  max-width: 100%;
}
.tabs button {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 9px;
  color: #69738b;
  display: flex;
  gap: 7px;
  padding: 10px 12px;
  white-space: nowrap;
}
.tabs button.active {
  background: #fff;
  color: var(--blue);
  font-weight: 800;
  box-shadow: 0 3px 10px rgba(35, 60, 75, 0.1);
}
.tabs span {
  background: #e7eaf2;
  border-radius: 10px;
  font-size: 10px;
  padding: 1px 6px;
}
.alert {
  align-items: center;
  background: #fff0f0;
  border: 1px solid #ffcfcf;
  border-radius: 10px;
  color: #b72d2d;
  display: flex;
  gap: 8px;
  margin-bottom: 15px;
  padding: 12px;
}
.alert button {
  background: transparent;
  border: 0;
  margin-left: auto;
}
.metrics {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 16px;
}
.metrics article {
  align-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  display: flex;
  gap: 14px;
  padding: 18px;
}
.metrics article > i {
  align-items: center;
  background: #edf1ff;
  border-radius: 12px;
  color: var(--blue);
  display: flex;
  font-size: 18px;
  height: 45px;
  justify-content: center;
  width: 45px;
}
.metrics small,
.metrics span {
  color: var(--muted);
  display: block;
}
.metrics strong {
  display: block;
  font-size: 20px;
  margin: 2px 0;
}
.metrics.small {
  grid-template-columns: repeat(2, 1fr);
}
.grid-2 {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}
.panel {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  margin-bottom: 16px;
  padding: 18px;
}
.section-head h3,
.toolbar h2 {
  margin: 0;
}
.section-head p,
.toolbar p {
  color: var(--muted);
  margin: 4px 0 14px;
}
.toolbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin: 8px 0 15px;
}
.aging {
  align-items: center;
  display: grid;
  gap: 10px;
  grid-template-columns: 110px 1fr 135px;
  margin: 15px 0;
}
.aging > div {
  background: #eef1f7;
  border-radius: 8px;
  height: 8px;
}
.aging i {
  background: linear-gradient(90deg, #3558e6, #56a7f5);
  border-radius: 8px;
  display: block;
  height: 100%;
}
.aging b {
  text-align: right;
}
.status-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
}
.status-grid article {
  background: var(--soft);
  border-radius: 10px;
  display: grid;
  gap: 5px;
  padding: 12px;
}
.status-grid span:last-child {
  color: var(--muted);
}
.guidance > div {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
}
.guidance > div span {
  background: var(--soft);
  border-radius: 9px;
  padding: 11px;
}
.guidance i {
  color: #16a270;
  margin-right: 8px;
}
.table-wrap {
  overflow: auto;
}
table {
  border-collapse: collapse;
  width: 100%;
}
th {
  color: #7d879c;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
td,
th {
  border-bottom: 1px solid #edf0f5;
  padding: 12px 10px;
  vertical-align: middle;
}
td small {
  color: var(--muted);
}
.badge {
  background: #eef1f5;
  border-radius: 20px;
  display: inline-block;
  font-size: 10px;
  font-weight: 800;
  padding: 5px 9px;
  text-transform: capitalize;
}
.badge.active,
.badge.approved,
.badge.paid,
.badge.verified,
.badge.completed {
  background: #e2f7ee;
  color: #16805c;
}
.badge.rejected,
.badge.declined,
.badge.cancelled,
.badge.expired {
  background: #ffe6e6;
  color: #b72d2d;
}
.badge.submitted,
.badge.under_review,
.badge.invoiced,
.badge.sent {
  background: #e7edff;
  color: #3558e6;
}
.badge.revision_required,
.badge.partially_approved,
.badge.partially_paid,
.badge.requested {
  background: #fff1d5;
  color: #a66b00;
}
.mini {
  background: #edf1ff;
  color: var(--blue);
  padding: 7px 10px;
}
.icon {
  background: transparent;
  color: var(--blue);
  padding: 7px;
}
.programs {
  display: grid;
  gap: 15px;
  grid-template-columns: repeat(3, 1fr);
}
.programs article {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  padding: 18px;
}
.program-head,
.programs footer {
  align-items: center;
  display: flex;
  justify-content: space-between;
}
.program-head > span {
  align-items: center;
  background: #edf1ff;
  border-radius: 10px;
  color: var(--blue);
  display: flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}
.programs h3 {
  margin: 14px 0 5px;
}
.programs p,
.programs small {
  color: var(--muted);
}
.progress {
  background: #edf0f5;
  border-radius: 6px;
  height: 7px;
  margin: 18px 0 7px;
}
.progress i {
  background: #3558e6;
  border-radius: 6px;
  display: block;
  height: 100%;
}
.programs footer {
  border-top: 1px solid var(--line);
  margin-top: 15px;
  padding-top: 14px;
}
.overlay {
  align-items: center;
  background: rgba(18, 25, 45, 0.55);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 20px;
  position: fixed;
  z-index: 1100;
}
.guarantor-modal {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.22);
  display: block;
  max-height: 92vh;
  max-width: 760px;
  overflow: auto;
  position: relative;
  width: 100%;
}
.guarantor-modal > header {
  align-items: flex-start;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
}
.guarantor-modal > header small {
  color: var(--blue);
  font-weight: 800;
  letter-spacing: 0.08em;
}
.guarantor-modal > header h2 {
  margin: 3px 0;
}
.guarantor-modal > header p {
  color: var(--muted);
  margin: 4px 0;
}
.guarantor-modal > header button {
  background: transparent;
  border: 0;
  font-size: 28px;
}
.form-body {
  display: grid;
  gap: 15px;
  grid-template-columns: 1fr 1fr;
  padding: 22px 24px;
}
.form-body label > span {
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 6px;
}
.form-body input,
.form-body select,
.form-body textarea {
  border: 1px solid #dce1eb;
  border-radius: 9px;
  min-height: 42px;
  padding: 9px 11px;
  width: 100%;
}
.lookup-trigger {
  align-items: center;
  background: #fff;
  border: 1px solid #d4e1e9;
  border-radius: 9px;
  color: #243b50;
  display: flex;
  gap: 10px;
  min-height: 43px;
  padding: 7px 11px;
  text-align: left;
  width: 100%;
}
.lookup-trigger:hover:not(:disabled),
.lookup-trigger:focus {
  background: #f8fcfc;
  border-color: #15978e;
  box-shadow: 0 0 0 3px rgba(21, 151, 142, 0.1);
  outline: 0;
}
.lookup-trigger:disabled { cursor: not-allowed; opacity: .65; }
.lookup-trigger__icon,
.lookup-list__icon {
  align-items: center;
  background: #e8f6f4;
  border-radius: 8px;
  color: #07877d;
  display: flex;
  flex: 0 0 30px;
  height: 30px;
  justify-content: center;
  width: 30px;
}
.lookup-trigger > span:nth-child(2) { flex: 1; min-width: 0; }
.lookup-trigger b,
.lookup-trigger small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lookup-trigger small { color: #718496; font-size: 10px; margin-top: 2px; }
.lookup-trigger .placeholder b { color: #65788a; }
.lookup-trigger__arrow { color: #168c84; }
.lookup-overlay { z-index: 1180; }
.lookup-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, .22);
  max-height: 86vh;
  max-width: 720px;
  overflow: hidden;
  width: 100%;
}
.lookup-modal > header {
  align-items: flex-start;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  padding: 19px 22px;
}
.lookup-modal > header small { color: var(--blue); font-weight: 800; letter-spacing: .08em; }
.lookup-modal > header h2 { margin: 3px 0; }
.lookup-modal > header p { color: var(--muted); margin: 3px 0 0; }
.lookup-modal > header button { background: transparent; border: 0; font-size: 28px; }
.lookup-search {
  align-items: center;
  background: #f6f9fb;
  display: flex;
  gap: 9px;
  padding: 14px 18px;
}
.lookup-search > i { color: #168c84; }
.lookup-search input { border: 1px solid #d4e1e9; border-radius: 9px; flex: 1; padding: 10px 12px; }
.lookup-search span { color: var(--muted); font-size: 11px; font-weight: 700; }
.lookup-list { max-height: 480px; overflow-y: auto; padding: 8px 16px 16px; }
.lookup-list > button {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 1px solid #edf2f5;
  color: #34495e;
  display: flex;
  gap: 11px;
  padding: 12px 8px;
  text-align: left;
  width: 100%;
}
.lookup-list > button:hover { background: #f4fbfa; }
.lookup-list > button > span:nth-child(2) { flex: 1; }
.lookup-list b,
.lookup-list small { display: block; }
.lookup-list small { color: var(--muted); margin-top: 3px; }
.lookup-list > button > i { color: #9aabb8; }
.form-body textarea {
  min-height: 75px;
}
.form-body .wide,
.claim-picker {
  grid-column: 1/-1;
}
.form-body .check {
  align-items: center;
  display: flex;
  gap: 8px;
}
.form-body .check input {
  min-height: 0;
  width: auto;
}
.guarantor-modal > footer {
  border-top: 1px solid var(--line);
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 16px 24px;
}
.claim-picker {
  border: 1px solid var(--line);
  border-radius: 10px;
  max-height: 260px;
  overflow: auto;
  padding: 8px;
}
.claim-picker label {
  align-items: center;
  border-bottom: 1px solid var(--line);
  display: flex;
  gap: 10px;
  padding: 9px;
}
.claim-picker input {
  width: auto;
}
.claim-picker span {
  display: flex;
  gap: 12px;
}
.claim-modal {
  max-width: 980px;
}
.claim-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 18px 24px;
}
.claim-summary div {
  border-right: 1px solid var(--line);
  padding: 0 15px;
}
.claim-summary small,
.claim-summary b {
  display: block;
}
.claim-columns {
  border-top: 1px solid var(--line);
  display: grid;
  gap: 25px;
  grid-template-columns: 1fr 1fr;
  padding: 22px 24px;
}
.claim-columns h3 {
  font-size: 14px;
  margin: 8px 0 13px;
}
.document,
.line {
  align-items: center;
  border-bottom: 1px solid var(--line);
  display: flex;
  gap: 10px;
  padding: 10px 0;
}
.document > span {
  flex: 1;
}
.document small {
  color: var(--muted);
  display: block;
}
.document .ok {
  color: #16a270;
}
.line {
  justify-content: space-between;
}
.workflow-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.workflow-actions button {
  border: 0;
  border-radius: 8px;
  padding: 8px 10px;
}
.workflow-actions i {
  margin-right: 6px;
}
.workflow-actions .primary {
  background: #3558e6;
  color: #fff;
}
.workflow-actions .warning {
  background: #fff1d5;
  color: #946100;
}
.workflow-actions .danger {
  background: #ffe6e6;
  color: #a82929;
}
.timeline {
  border-left: 2px solid #e6eaf2;
  margin-left: 6px;
}
.timeline article {
  display: flex;
  gap: 12px;
  margin: 0 0 16px 14px;
}
.timeline article > i {
  background: #3558e6;
  border: 3px solid #e5eaff;
  border-radius: 50%;
  height: 12px;
  margin-left: -21px;
  margin-top: 4px;
  width: 12px;
}
.timeline b,
.timeline span,
.timeline small {
  display: block;
}
.timeline span,
.timeline small {
  color: var(--muted);
}
.timeline p {
  margin: 4px 0;
}
.empty {
  color: var(--muted);
  padding: 20px;
  text-align: center;
}
@media (max-width: 1100px) {
  .metrics {
    grid-template-columns: 1fr 1fr;
  }
  .guidance > div {
    grid-template-columns: 1fr 1fr;
  }
  .programs {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 700px) {
  .grid-2,
  .claim-columns {
    grid-template-columns: 1fr;
  }
  .metrics,
  .metrics.small,
  .programs {
    grid-template-columns: 1fr;
  }
  .guidance > div {
    grid-template-columns: 1fr;
  }
  .toolbar {
    align-items: flex-start;
    gap: 10px;
  }
  .form-body {
    grid-template-columns: 1fr;
  }
  .claim-summary {
    grid-template-columns: 1fr 1fr;
    gap: 15px;
  }
  .aging {
    grid-template-columns: 90px 1fr;
  }
  .aging b {
    grid-column: 2;
  }
  .guarantor-modal {
    max-height: 96vh;
  }
}
</style>
